import clsx from 'clsx';
import { useMemo } from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  OnOutlierSettingsChangeArgs,
  useAutoPricingSettingsFormContext,
} from 'src/components/AutoPricing/useAutoPricingSettingsFormContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { PosEnumSelect, PosSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Button, Popover, Stack } from 'src/core/ui';
import { ToggleGroup } from 'src/core/ui/ToggleGroup';
import { useAvailableAutopricingModes } from 'src/hooks/useAvailableAutopricingModes';
import { ExpandIcon, FoldIcon } from 'src/svgs/Viagogo';
import {
  OUTLIER_MODE_KTH_LOWEST_STRATEGY,
  OutlierModeKthLowestStrategy,
} from 'src/utils/autoPricingUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { STANDARD_DEVIATIONS_TO_CID } from 'src/utils/constants/contentIdMaps';
import { numberToOrdinal } from 'src/utils/numberFormatter';
import { ListingDetailsAutoPricingSectionUpdates } from 'src/WebApiController';
import { AutoPricingOutlierMode } from 'src/WebApiController';

import * as styles from './PricingSetting.css';

type OutlierSettingsProps = {
  useVerticalView: boolean;
};

export const OutlierSettings = ({ useVerticalView }: OutlierSettingsProps) => {
  const { watch } = useFormContext<ListingDetailsAutoPricingSectionUpdates>();
  const {
    outlierMode,
    outlierKthLowestLimit,
    outlierKthLowestLimitAbsoluteSpacing,
    outlierKthLowestLimitRelativeSpacing,
    outlierStandardDeviations,
  } = watch();
  const [showOutlierSettings, setShowOutlierSettings] =
    useState<boolean>(false);
  const { onOutlierSettingsChange } = useAutoPricingSettingsFormContext();

  const { outlierModes } = useAvailableAutopricingModes(
    true,
    null,
    null,
    true,
    false,
    false,
    true
  );

  if (useVerticalView) {
    return (
      <Popover.Root>
        <Popover.Trigger asChild>
          <Button
            variant="textPlain"
            style={{
              padding: vars.spacing['xxs'],
              whiteSpace: 'nowrap',
            }}
          >
            <div style={{ fontWeight: 400 }}>
              <Content id={ContentId.Outlier} />{' '}
              <Content id={ContentId.Settings} />
            </div>
          </Button>
        </Popover.Trigger>
        <Popover.Content side="top">
          <Stack
            width="full"
            direction="column"
            gap="m"
            className={styles.outlierSettingsPopoverContainer}
          >
            <PosEnumSelect
              value={outlierMode}
              valueOptionsContent={outlierModes}
              enableEmptySelection={false}
              className={clsx(styles.inputInline, styles.selectContainer)}
              triggerContentClassName={styles.selectTriggerContent}
              prefixDisplay={
                <span className={styles.inputPrefix}>
                  <Content id={ContentId.DetectionMode} />
                </span>
              }
              onClick={(e) => e.stopPropagation()}
              onChange={(v) =>
                onOutlierSettingsChange({
                  outlierModeNew: v as AutoPricingOutlierMode,
                })
              }
            />
            {outlierMode === AutoPricingOutlierMode.KthLowest ? (
              <KthLowestOutlierDetectionSettingsInput
                outlierKthLowestLimit={outlierKthLowestLimit}
                outlierKthLowestLimitAbsoluteSpacing={
                  outlierKthLowestLimitAbsoluteSpacing
                }
                outlierKthLowestLimitRelativeSpacing={
                  outlierKthLowestLimitRelativeSpacing
                }
                onOutlierSettingsChange={onOutlierSettingsChange}
              />
            ) : outlierMode === AutoPricingOutlierMode.StandardDeviations ? (
              <StandardDeviationOutlierDetectionSettingsInput
                outlierStandardDeviations={outlierStandardDeviations}
                onOutlierSettingsChange={onOutlierSettingsChange}
              />
            ) : null}
          </Stack>
        </Popover.Content>
      </Popover.Root>
    );
  }

  return (
    <div className={styles.settingsWrapper}>
      <Stack
        justifyContent="spaceBetween"
        alignItems="center"
        className={clsx(styles.settingsHeaderContainer, {
          [styles.settingsHeaderContainerHide]: !showOutlierSettings,
        })}
        onClick={() => setShowOutlierSettings((prev) => !prev)}
      >
        <span>
          <Content id={ContentId.Outlier} /> <Content id={ContentId.Settings} />
        </span>
        {showOutlierSettings ? (
          <FoldIcon size={vars.iconSize.s} />
        ) : (
          <ExpandIcon size={vars.iconSize.s} />
        )}
      </Stack>
      {showOutlierSettings ? (
        <div className={styles.outlierSettingsInnerLayout}>
          <PosEnumSelect
            value={outlierMode}
            valueOptionsContent={outlierModes}
            enableEmptySelection={false}
            className={clsx(styles.inputInline, styles.selectContainer)}
            triggerContentClassName={styles.selectTriggerContent}
            prefixDisplay={
              <span className={styles.inputPrefix}>
                <Content id={ContentId.DetectionMode} />
              </span>
            }
            onClick={(e) => e.stopPropagation()}
            onChange={(v) =>
              onOutlierSettingsChange({
                outlierModeNew: v as AutoPricingOutlierMode,
              })
            }
          />
          {outlierMode === AutoPricingOutlierMode.KthLowest ? (
            <KthLowestOutlierDetectionSettingsInput
              outlierKthLowestLimit={outlierKthLowestLimit}
              outlierKthLowestLimitAbsoluteSpacing={
                outlierKthLowestLimitAbsoluteSpacing
              }
              outlierKthLowestLimitRelativeSpacing={
                outlierKthLowestLimitRelativeSpacing
              }
              onOutlierSettingsChange={onOutlierSettingsChange}
            />
          ) : outlierMode === AutoPricingOutlierMode.StandardDeviations ? (
            <StandardDeviationOutlierDetectionSettingsInput
              outlierStandardDeviations={outlierStandardDeviations}
              onOutlierSettingsChange={onOutlierSettingsChange}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const KthLowestOutlierDetectionSettingsInput = ({
  outlierKthLowestLimit,
  outlierKthLowestLimitRelativeSpacing,
  outlierKthLowestLimitAbsoluteSpacing,
  onOutlierSettingsChange,
}: {
  outlierKthLowestLimit: number | null;
  outlierKthLowestLimitRelativeSpacing: number | null;
  outlierKthLowestLimitAbsoluteSpacing: number | null;
  onOutlierSettingsChange: (args: OnOutlierSettingsChangeArgs) => void;
}) => {
  const [outlierKthLowestStrategy, setKthLowestOutlierStrategy] = useState(
    OutlierModeKthLowestStrategy.Relative
  );

  const ignoreListingsText = useContent(
    ContentId.OutlierSettingsIngoreListings
  );
  const belowTheText = useContent(ContentId.BelowThe);
  const cheapestText = useContent(ContentId.Cheapest);

  const lowestLimitOptions = useMemo(() => {
    const options: Record<string, string> = {};
    for (const [index, _] of Array.from({ length: 100 }).entries()) {
      if (index <= 0) continue;
      const value = index + 1;
      options[index] = `${numberToOrdinal(
        value
      )} ${cheapestText.toLocaleLowerCase()}`;
    }
    return options;
  }, [cheapestText]);

  return (
    <>
      <Stack gap="m" direction="column" alignItems="center" width="full">
        <Stack gap="s" alignItems="center" width="full">
          {outlierKthLowestStrategy == OutlierModeKthLowestStrategy.Relative ? (
            <PosTextField
              type="number"
              prefixDisplay={
                <span className={styles.inputPrefix}>{ignoreListingsText}</span>
              }
              min={-1}
              max={100}
              rootProps={{ variant: 'small', className: styles.baseInput }}
              className={clsx(styles.inputInline, styles.textAlignRight)}
              value={outlierKthLowestLimitRelativeSpacing ?? ''}
              onChange={(e) => {
                let v = parseInt(e.target.value);
                if (v < 0) {
                  v = 0;
                } else if (v > 100) {
                  v = 100;
                }

                if (!isNaN(v)) {
                  onOutlierSettingsChange({
                    outlierModeNew: AutoPricingOutlierMode.KthLowest,
                    outlierKthLowestLimitRelativeSpacingNew: v,
                    outlierKthLowestLimitAbsoluteSpacingNew: null,
                  });
                }
              }}
            />
          ) : (
            <PosTextField
              type="number"
              prefixDisplay={
                <span className={styles.inputPrefix}>{ignoreListingsText}</span>
              }
              rootProps={{ variant: 'small', className: styles.baseInput }}
              className={clsx(styles.inputInline, styles.textAlignRight)}
              value={outlierKthLowestLimitAbsoluteSpacing ?? 0}
              onChange={(e) => {
                const v = parseFloat(e.target.value);

                if (!isNaN(v)) {
                  onOutlierSettingsChange({
                    outlierModeNew: AutoPricingOutlierMode.KthLowest,
                    outlierKthLowestLimitRelativeSpacingNew: null,
                    outlierKthLowestLimitAbsoluteSpacingNew: v,
                  });
                }
              }}
            />
          )}
          <ToggleGroup
            options={OUTLIER_MODE_KTH_LOWEST_STRATEGY}
            value={outlierKthLowestStrategy}
            itemClassName={styles.toggleGroupItem}
            itemTransparent
            onValueChange={(value) => {
              if (!value) {
                return;
              }

              const strat = value as OutlierModeKthLowestStrategy;
              setKthLowestOutlierStrategy(strat);
              onOutlierSettingsChange({
                outlierModeNew: AutoPricingOutlierMode.KthLowest,
                outlierKthLowestLimitRelativeSpacingNew: null,
                outlierKthLowestLimitAbsoluteSpacingNew: null,
              });
            }}
          />
        </Stack>
        <div className={styles.outlierSettingsInnerLayout}>
          <PosSelect
            value={outlierKthLowestLimit ? outlierKthLowestLimit + '' : ''}
            valueOptionsContent={lowestLimitOptions}
            className={clsx(styles.inputInline, styles.selectContainer)}
            triggerContentClassName={styles.selectTriggerContent}
            onClick={(e) => e.stopPropagation()}
            prefixDisplay={
              <span className={styles.inputPrefix}>{belowTheText}</span>
            }
            contentProps={{ style: { maxHeight: '400px' } }}
            onChange={(v) => {
              const value = parseInt(v);
              if (!isNaN(value)) {
                onOutlierSettingsChange({
                  outlierModeNew: AutoPricingOutlierMode.KthLowest,
                  outlierKthLowestLimitNew: value,
                });
              }
            }}
          />
        </div>
      </Stack>
    </>
  );
};

const StandardDeviationOutlierDetectionSettingsInput = ({
  outlierStandardDeviations,
  onOutlierSettingsChange,
}: {
  outlierStandardDeviations: number | null;
  onOutlierSettingsChange: (input: OnOutlierSettingsChangeArgs) => void;
}) => {
  return (
    <PosEnumSelect
      className={styles.selectContainer}
      value={outlierStandardDeviations?.toString()}
      valueOptionsContent={STANDARD_DEVIATIONS_TO_CID}
      enableEmptySelection={true}
      onClick={(e) => e.stopPropagation()}
      onChange={(v) => {
        const value = parseInt(v ?? '');
        if (value != null && !isNaN(value)) {
          onOutlierSettingsChange({
            outlierModeNew: AutoPricingOutlierMode.StandardDeviations,
            outlierStandardDeviationsNew: value,
          });
        } else {
          onOutlierSettingsChange({
            outlierModeNew: AutoPricingOutlierMode.StandardDeviations,
            outlierStandardDeviationsNew: null,
          });
        }
      }}
    />
  );
};
